import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}>
    {children}
  </motion.div>
);

const VALUES = [
  { num: "01", title: "Quality Craftsmanship", text: "The details you never see get the same obsession as the ones you do. Straight, true and built to outlast trends." },
  { num: "02", title: "Straight Talk", text: "Clear pricing, honest programmes and a builder who answers the phone. No surprises buried in the fine print." },
  { num: "03", title: "Wairarapa Proud", text: "Local crew, local suppliers, local knowledge. We build for the climate and the community we live in." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="bg-white">
      {/* HEADER */}
      <section className="pt-56 pb-20 sm:pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">About LW&Co</motion.p>
          <div className="overflow-hidden">
            <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
              data-testid="about-headline">
              Built on craft.
            </motion.h1>
          </div>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="origin-left h-[3px] w-28 bg-gold mt-10" />
        </div>
      </section>

      {/* STORY */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="text-lg sm:text-xl text-black/70 leading-relaxed">
              LW&Co Building Company Limited is a Masterton-based building company, incorporated in 2017
              and raised on Wairarapa soil. We build exceptional homes and sharp commercial spaces —
              and we do it with a small, deliberate team who treat every project like it carries our name.
              Because it does.
            </p>
            <p className="mt-6 text-base sm:text-lg text-black/60 leading-relaxed">
              From architectural new builds and thoughtful renovations to commercial fit-outs, our work
              spans the region — Masterton, Carterton, Greytown, Martinborough and Featherston. What never
              changes is the standard: quality craftsmanship, honest communication and a finish we're
              proud to photograph.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-black/10 pt-8" data-testid="about-page-stats">
              {[["2017", "Incorporated"], ["Masterton", "Home Base"], ["R + C", "Residential & Commercial"]].map(([v, l]) => (
                <div key={l}>
                  <div className="font-display font-semibold text-xl sm:text-2xl">{v}</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mt-1.5">{l}</div>
                </div>
              ))}
            </div>
            <div data-testid="about-press-mention"
              className="mt-8 inline-flex items-center gap-3 border border-gold/60 px-5 py-3.5">
              <Newspaper size={16} className="text-gold" strokeWidth={1.75} />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/70">
                As featured in Business Central — August 2026
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <img src="/images/real-commercial-site.jpg" alt="LW&Co crew on site — Wairarapa commercial build"
              className="w-full aspect-[4/5] object-cover" data-testid="about-story-image" />
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="pb-20 sm:pb-28" data-testid="about-services">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="mb-12">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">What we do</p>
            <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-4xl">
              Residential. Commercial. Renovations.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {[
              { title: "Residential Builds", text: "Architectural new homes and our own 13-design plan range — from compact 50m² dwellings to designer family homes, built by our Masterton crew." },
              { title: "Commercial Builds", text: "Fit-outs, workshops and commercial spaces delivered on programme and on budget — with the same finish standard we put into our homes." },
              { title: "Renovations", text: "From full home transformations to kitchen and bathroom upgrades — no job is too big or too small. LW&Co brings the same quality and craftsmanship to every renovation project." },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div data-testid={`about-service-${i + 1}`} className="group bg-white p-8 sm:p-10 h-full hover:bg-black transition-colors duration-500">
                  <span className="text-[11px] font-bold tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-semibold uppercase text-xl sm:text-2xl mt-4 tracking-tight group-hover:text-white transition-colors duration-500">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm text-black/55 group-hover:text-white/60 leading-relaxed transition-colors duration-500">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section className="relative h-[50vh] sm:h-[65vh] overflow-hidden bg-black" data-testid="about-image-break">
        <motion.img initial={{ scale: 1.1 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          src="/images/real-concrete-pour.jpg" alt="LW&Co crew on a foundation pour under a Wairarapa sky"
          className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
          <p className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-10 w-full font-display font-semibold uppercase text-white text-2xl sm:text-4xl tracking-tight">
            Full crew on the pour, under a Wairarapa sky.
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display font-semibold uppercase tracking-tight text-4xl sm:text-5xl mb-14">How we work</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.1}>
                <div data-testid={`value-card-${v.num}`} className="bg-white p-8 sm:p-10 h-full group">
                  <span className="text-[11px] font-bold tracking-[0.3em] text-gold">{v.num}</span>
                  <h3 className="font-display font-semibold uppercase text-xl sm:text-2xl mt-4 tracking-tight group-hover:text-gold transition-colors duration-300">
                    {v.title}
                  </h3>
                  <p className="mt-4 text-sm text-black/55 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <Link to="/contact" data-testid="about-cta-btn"
              className="group inline-flex items-center gap-2 px-10 py-4 bg-black text-white font-bold text-sm uppercase tracking-[0.15em] hover:bg-gold hover:text-black transition-colors duration-300">
              Start your project <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
